package org.example.project.service

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object LoginService {
    private const val LEGACY_USER_STORAGE_KEY = "lumatica.login.user"
    private const val LEGACY_TS_STORAGE_KEY = "lumatica.login.ts"
    private const val SESSION_CONTEXT_STORAGE_KEY = "lumatica.session.context"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    private var user: All? = null

    private fun clearStorage() {
        runCatching {
            window.localStorage.removeItem(LEGACY_USER_STORAGE_KEY)
            window.localStorage.removeItem(LEGACY_TS_STORAGE_KEY)
        }
    }

    private fun sanitizeUser(userObj: All): All =
        userObj

    private fun clearDriverApkLogin() {
        js(
            """
            (function() {
                try {
                    var bridge = window.LumaticaFahrerApp;
                    if (bridge && bridge.clearLogin) bridge.clearLogin();
                } catch (e) {}
            })()
            """
        )
    }

    fun captureSession(userObj: All) {
        if (userObj.id.isNotBlank() && userObj.firmenId.isNotBlank()) {
            clearStorage()
            runCatching { window.sessionStorage.setItem(SESSION_CONTEXT_STORAGE_KEY, "${userObj.firmenId}\n${userObj.id}") }
        }
    }

    private fun sessionContext(): Pair<String, String> {
        val stored = runCatching { window.sessionStorage.getItem(SESSION_CONTEXT_STORAGE_KEY).orEmpty() }.getOrDefault("")
        val parts = stored.split('\n', limit = 2)
        return if (parts.size == 2) parts[0] to parts[1] else "" to ""
    }

    fun getFirmenId(): String = getUser()?.firmenId.orEmpty().ifBlank { sessionContext().first }

    fun sessionHeaders(): Headers = Headers().apply {
        val userId = getId().ifBlank { sessionContext().second }
        val firmenId = getFirmenId()
        if (userId.isNotBlank()) append("x-user-id", userId)
        if (firmenId.isNotBlank()) append("x-firmen-id", firmenId)
    }

    fun login(userObj: All) {
        org.example.project.screens_driver.clearDriverDocumentShares()
        captureSession(userObj)
        user = if (userObj.id.isNotBlank()) sanitizeUser(userObj) else null
    }

    fun logout() {
        org.example.project.screens_driver.clearDriverDocumentShares()
        runCatching {
            window.fetch(
                "${LumaticaApiSettings.getBaseUrl()}/api/session/logout",
                RequestInit(method = "POST", headers = sessionHeaders(), keepalive = true)
            )
        }
        user = null
        runCatching { window.sessionStorage.removeItem(SESSION_CONTEXT_STORAGE_KEY) }
        clearStorage()
        clearDriverApkLogin()
    }

    fun clearLocalLogin() {
        logout()
    }

    suspend fun restoreSession(): Boolean {
        val raw = runCatching {
            val response = window.fetch(
                "${LumaticaApiSettings.getBaseUrl()}/api/session/current"
                , RequestInit(headers = sessionHeaders())
            ).await()
            if (!response.ok) "" else response.text().await()
        }.getOrDefault("")
        val restoredUser = if (raw.isBlank() || raw == "null") null else {
            runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
        }
        if (restoredUser == null) {
            user = null
            clearStorage()
            return false
        }
        user = sanitizeUser(restoredUser)
        clearStorage()
        return true
    }

    fun getUser(): All? = user
    fun isLoggedIn(): Boolean = getUser() != null
    fun getId(): String = getUser()?.id.orEmpty().ifBlank { sessionContext().second }

    fun updateUser(transform: (All) -> All) {
        user = user?.let(transform)?.let(::sanitizeUser)
    }

    fun getKuerzel(): String {
        val u = getUser() ?: return ""

        fun twoLetters(value: String): String {
            val cleaned = value.filter { it.isLetter() }
            return when {
                cleaned.isEmpty() -> ""
                cleaned.length == 1 -> cleaned.uppercase()
                else -> cleaned.substring(0, 1).uppercase() + cleaned.substring(1, 2).lowercase()
            }
        }

        val kuerzel = twoLetters(u.vorname) + twoLetters(u.nachname)
        return kuerzel.ifBlank { u.personalnummer }
    }

    suspend fun checkUserLogin(userId: String, passwort: String): Boolean {
        val raw = runCatching {
            val response = window.fetch(
                "${LumaticaApiSettings.getBaseUrl()}/api/desktoploginscreen/login-json",
                RequestInit(
                    method = "POST",
                    headers = Headers().apply { append("Content-Type", "application/json") },
                    body = json.encodeToString(
                        buildJsonObject {
                            put("kennung", JsonPrimitive(userId.trim()))
                            put("password", JsonPrimitive(passwort))
                        }
                    )
                )
            ).await()
            response.text().await()
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return false
        val loginUser = runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
            ?: return false
        captureSession(loginUser)
        return true
    }
}
